import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { BlockDto } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { FollowButton } from '../components/FollowButton';
import { Cover, ItemCard } from '../components/cards';
import { IconLayers, IconLock, IconPencil, IconText } from '../components/icons';
import { useToast } from '../components/Toast';
import { Avatar, EmptyState, ErrorState, Loading, VisibilityBadge } from '../components/ui';
import { useAsync } from '../hooks/useAsync';
import { itemsWord, relativeTime } from '../lib/format';
import { t } from '../i18n';

function BlockView({ block, index }: { block: BlockDto; index: number }) {
  if (block.type === 'text') {
    return (
      <article className="block">
        <p className="block__note">
          <IconText size={12} /> {t('playlist.authorNote')}
        </p>
        <p className="block__text">{block.content}</p>
      </article>
    );
  }
  if (!block.item) return null;
  return (
    <article className="block">
      <p className="block__note">
        {t('playlist.blockNumber', { n: String(index + 1).padStart(2, '0') })}
      </p>
      <ItemCard item={block.item} to={block.item.url} external variant="route" />
    </article>
  );
}

export function PlaylistViewPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const state = useAsync(() => api.playlists.get(id), [id]);

  const playlist = state.data;

  if (state.error) {
    const error = state.error;
    const forbidden = error instanceof ApiError && (error.status === 403 || error.status === 404);
    return (
      <div className="page page--read">
        {forbidden ? (
          <EmptyState
            icon={<IconLock />}
            title={error.status === 403 ? t('playlist.hiddenTitle') : t('playlist.notFoundTitle')}
            body={
              error.status === 403
                ? t('playlist.hiddenBody')
                : t('playlist.notFoundBody')
            }
            action={
              !user ? (
                <Link className="btn btn--primary" to="/login">
                  {t('nav.enter')}
                </Link>
              ) : (
                <Link className="btn btn--outline" to="/app/playlists">
                  {t('playlist.myPlaylists')}
                </Link>
              )
            }
          />
        ) : (
          <ErrorState error={error} onRetry={state.reload} />
        )}
      </div>
    );
  }

  if (state.loading || !playlist) {
    return (
      <div className="page page--read">
        <Loading rows={4} />
      </div>
    );
  }

  const numbered = playlist.blocks.map((block) => ({ block, itemNumber: -1 }));
  let seen = 0;
  for (const entry of numbered) {
    if (entry.block.type === 'item' && entry.block.item) entry.itemNumber = seen++;
  }

  async function share() {
    const url = `${window.location.origin}/p/${playlist!.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('playlist.linkCopied'));
    } catch {
      toast.push(url);
    }
  }

  return (
    <div className="page page--read">
      <header className="playlist-head">
        <Cover src={playlist.coverImageUrl} title={playlist.title} className="playlist-head__cover" eager />
        <div className="row row--between">
          <VisibilityBadge visibility={playlist.visibility} />
          <span className="tiny muted">{t('playlist.updated', { time: relativeTime(playlist.updatedAt) })}</span>
        </div>
        <h1 className="playlist-head__title">{playlist.title}</h1>
        {playlist.description ? <p className="dim">{playlist.description}</p> : null}

        <div className="playlist-head__author">
          <Avatar name={playlist.owner.displayName} src={playlist.owner.avatarUrl} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Link to={`/u/${playlist.owner.username}`} className="strong">
              {playlist.owner.displayName}
            </Link>
            <p className="tiny muted">
              @{playlist.owner.username} · {itemsWord(playlist.itemCount)}
            </p>
          </div>
          <FollowButton userId={playlist.owner.id} following={playlist.viewer.isFollowing} size="sm" />
        </div>

        <div className="row">
          {playlist.viewer.canEdit ? (
            <>
              <button type="button" className="btn btn--primary" onClick={() => navigate(`/app/playlists/${playlist.id}/edit`)}>
                <IconPencil size={17} /> {t('playlist.edit')}
              </button>
              <button type="button" className="btn btn--ghost" onClick={share}>
                {t('playlist.copyLink')}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--outline" onClick={share}>
              {t('playlist.share')}
            </button>
          )}
        </div>
      </header>

      {playlist.blocks.length === 0 ? (
        <EmptyState
          icon={<IconLayers />}
          title={t('playlist.isEmpty')}
          body={playlist.viewer.canEdit ? t('playlist.isEmptyMine') : t('playlist.isEmptyTheirs')}
          action={
            playlist.viewer.canEdit ? (
              <Link className="btn btn--primary" to={`/app/playlists/${playlist.id}/edit`}>
                {t('playlist.openEditor')}
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="blocks">
          {numbered.map(({ block, itemNumber }) => (
            <BlockView key={block.id} block={block} index={itemNumber} />
          ))}
        </div>
      )}
    </div>
  );
}
